/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, {
	useState,
	useRef,
	useEffect,
	useImperativeHandle,
	forwardRef,
} from 'react';
import { Button, Alert } from 'react-bootstrap';
import {
	BsCrop,
	BsCardImage,
	BsImage,
	BsFileImage,
} from 'react-icons/bs';
import CircleImage from '../Icons/CircleImage';
import BayModal from '../BayModal';
import BaySpinner from '../BaySpinner';
import BayButton from '../BayAddButton';
import { WideLayout } from './layouts';
import {
	SplitLayout,
	ToolbarControls,
	AdjustmentControls,
} from './layouts/SplitLayout';
import styles from './BayImageCropModal.module.scss';

/**
 * BayImageCropModal - Advanced image cropping component with zoom, pan, rotate, and flip capabilities
 *
 * @example
 * // Wide layout (default) - Full width for email editor
 * <BayImageCropModal
 *   show={showModal}
 *   onHide={() => setShowModal(false)}
 *   imageURL="https://example.com/image.jpg"
 *   onCropComplete={(blob, mimeType) => handleUpload(blob)}
 *   layout="wide"
 * />
 *
 * @example
 * // Split layout - Crop on left, custom content on right
 * <BayImageCropModal
 *   show={showModal}
 *   onHide={() => setShowModal(false)}
 *   imageURL="https://example.com/image.jpg"
 *   onCropComplete={(blob, mimeType) => handleUpload(blob)}
 *   layout="split"
 *   splitContent={<div>Preview or additional options</div>}
 * />
 *
 * @example
 * // With ref for imperative save
 * const cropperRef = useRef<BayImageCropModalHandle>(null);
 *
 * <BayImageCropModal
 *   ref={cropperRef}
 *   show={showModal}
 *   onHide={() => setShowModal(false)}
 *   imageURL="https://example.com/image.jpg"
 *   onCropComplete={(blob, mimeType) => handleUpload(blob)}
 * />
 *
 * // Trigger save programmatically
 * await cropperRef.current?.triggerSave();
 */

export interface BayImageCropModalProps {
	/** Controls modal visibility */
	show: boolean;
	/** Render cropper in place without wrapping BayModal */
	embedded?: boolean;
	/** Place crop controls above or below the image area */
	controlPlacement?: 'top' | 'bottom';
	/** Callback when modal should close */
	onHide: () => void;
	/** Source image URL */
	imageURL: string;
	/** Callback when crop is completed - receives blob and mime type */
	onCropComplete: (_blob: Blob, _mimeType: 'image/png' | 'image/jpeg') => void;
	/** Output image format (defaults to 'image/jpeg', circular crops force 'image/png') */
	outputMimeType?: 'image/png' | 'image/jpeg';
	/** Layout variant - 'wide' for full width, 'split' for two columns */
	layout?: 'wide' | 'split';
	/** Content to display in right column when layout='split' */
	splitContent?: React.ReactNode;
	/**
	 * When layout='split', overrides the layout Row's className entirely
	 * (replaces the default `g-0 bay-image-crop-split-layout flex-fill
	 * h-100`, not merged -- required to win a height override against the
	 * hardcoded `h-100` utility class's own `!important`).
	 */
	splitLayoutClassName?: string;
	/** When layout='split', merged onto the layout Row's default inline style (`{ minHeight: 0 }`). */
	splitLayoutStyle?: React.CSSProperties;
	/** When layout='split', merged onto the cropper column's default inline style (`{ minHeight: 0 }`). */
	splitMainStyle?: React.CSSProperties;
	/** Show or hide the crop shape dropdown */
	showCropShapeControl?: boolean;
	/** Slider UI mode - toggle between sliders or show both stacked */
	sliderLayout?: SliderLayout;
	/** Show datalist tick marks/labels for rotation slider */
	showRotationTickMarks?: boolean;
	/** Show datalist tick marks for scale slider */
	showScaleTickMarks?: boolean;
	/** Show min/mid/max percent labels under scale slider */
	showScaleRangeLabels?: boolean;
	/** Custom save handler (optional, defaults to internal save logic) */
	onSave?: () => Promise<void>;
	/** Disable all controls during upload/save */
	isUploading?: boolean;
	/** Callback when user adjusts crop settings (zoom/pan/rotate/resize/transform/reset) */
	onUserInteraction?: () => void;
	/** Optional saved crop UI state to restore on mount/image change */
	initialCropState?: Partial<BayImageCropState>;
	/** Callback when a crop is saved, returning the current crop UI state */
	onCropStateSaved?: (_state: BayImageCropState) => void;
	/** Lock the initial aspect ratio (width/height). Applied on mount; respected even in free-form mode. */
	initialAspectRatio?: number;
	/** Enable circular crop mask on mount. */
	initialCircularCrop?: boolean;
	/** Exact pixel width to scale the exported crop to. */
	outputWidth?: number;
	/** Exact pixel height to scale the exported crop to. */
	outputHeight?: number;
	/** How the source image should fit in the crop viewport before user zoom/pan. */
	imageFitMode?: 'contain' | 'cover';
	/** Translation function for i18n */
	t?: (_key: string) => string;
	/** Modal title (defaults to "Crop Image") */
	title?: string;
	/** Modal size override (defaults based on layout) */
	size?: 'sm' | 'lg' | 'xl';
	/** Force fullscreen modal */
	fullscreen?: boolean;
	/** Hide the permanent-crop warning alert */
	hideWarningAlert?: boolean;
	/** Optional class name for the cropper content wrapper */
	cropperContentClassName?: string;
	/** Optional style overrides for the cropper content wrapper */
	cropperContentStyle?: React.CSSProperties;
	/** Optional class name for the crop container element */
	cropContainerClassName?: string;
	/** Optional style overrides for the crop container element */
	cropContainerStyle?: React.CSSProperties;
	/** Optional maximum crop frame width in pixels */
	maxCropFrameWidth?: number;
	/** Optional maximum crop frame height in pixels */
	maxCropFrameHeight?: number;
	/** Optional image viewport width in pixels (centered inside crop container) */
	imageLayerWidth?: number;
	/** Optional image viewport height in pixels (centered inside crop container) */
	imageLayerHeight?: number;
	/** Show options for controlling crop shape (aspect ratio presets, circular toggle) */
	showCropShapeControlOptions?: boolean;
	/** Show a combined rotation/scale slider view separately */
	isSplitRotationView?: boolean;
	/** Optional percentage offset applied only to zoom UI labels (does not change internal zoom scale) */
	zoomDisplayOffsetPercent?: number;
}

export interface BayImageCropModalHandle {
	/** Programmatically trigger the save/crop operation */
	triggerSave: () => Promise<void>;
}

export interface BayImageCropState {
	activePreset: AspectPreset;
	aspectRatio?: number;
	circularCrop: boolean;
	cropFrame: { width: number; height: number };
	isFlippedHorizontally?: boolean;
	pan: { x: number; y: number };
	rotationDegrees: number;
	zoom: number;
}

export type AspectPreset = 'free' | 'square' | '16:9' | '4:3' | '4:5' | 'circle';
type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
export type ControlMode = 'rotation' | 'scale';
export type SliderLayout = 'toggle' | 'stacked';

const PRESET_LABELS: Record<AspectPreset, string> = {
	free: 'Freeform',
	square: 'Square',
	'16:9': 'Wide (16:9)',
	'4:3': 'Landscape (4:3)',
	'4:5': 'Portrait (4:5)',
	circle: 'Circle',
};

const PRESET_ICONS: Record<
	AspectPreset,
	React.ComponentType<{ size?: number; className?: string }>
> = {
	free: BsCrop,
	square: BsImage,
	'16:9': BsCardImage,
	'4:3': BsImage,
	'4:5': BsFileImage,
	circle: CircleImage,
};

const defaultTranslate = (key: string) => key;

const BayImageCropModal = forwardRef<
	BayImageCropModalHandle,
	BayImageCropModalProps
>(
	(
		{
			show,
			embedded = false,
			controlPlacement = 'top',
			onHide,
			imageURL,
			onCropComplete,
			outputMimeType = 'image/jpeg',
			layout = 'wide',
			splitContent,
			splitLayoutClassName,
			splitLayoutStyle,
			splitMainStyle,
			showCropShapeControl = true,
			sliderLayout = 'toggle',
			showRotationTickMarks = true,
			showScaleTickMarks = true,
			showScaleRangeLabels = true,
			onSave,
			isUploading = false,
			onUserInteraction,
			initialCropState,
			onCropStateSaved,
			initialAspectRatio,
			initialCircularCrop = false,
			outputWidth,
			outputHeight,
			imageFitMode = 'contain',
			t = defaultTranslate,
			title,
			size,
			fullscreen,
			hideWarningAlert = false,
			cropperContentClassName,
			cropperContentStyle,
			cropContainerClassName,
			cropContainerStyle,
			showCropShapeControlOptions = false,
			isSplitRotationView = false,
			zoomDisplayOffsetPercent = 0,
			maxCropFrameWidth,
			maxCropFrameHeight,
			imageLayerWidth,
			imageLayerHeight,
		},
		ref,
	) => {
		const [zoom, setZoom] = useState(initialCropState?.zoom ?? 1);
		const [aspectRatio, setAspectRatio] = useState<number | undefined>(
			initialCropState?.aspectRatio ?? initialAspectRatio,
		);
		const [circularCrop, setCircularCrop] = useState(
			initialCropState?.circularCrop ?? initialCircularCrop,
		);
		const [activePreset, setActivePreset] = useState<AspectPreset>(
			initialCropState?.activePreset ?? 'free',
		);
		const [isPanning, setIsPanning] = useState(false);
		const [isResizing, setIsResizing] = useState(false);
		const [imageNaturalSize, setImageNaturalSize] = useState({
			width: 0,
			height: 0,
		});
		const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
		const [cropFrame, setCropFrame] = useState(
			initialCropState?.cropFrame || { width: 320, height: 320 },
		);
		const [isFlippedHorizontally, setIsFlippedHorizontally] = useState(
			initialCropState?.isFlippedHorizontally ?? false,
		);
		const [pan, setPan] = useState(initialCropState?.pan || { x: 0, y: 0 });
		const [workingImageURL, setWorkingImageURL] = useState(imageURL);
		const [controlMode, setControlMode] = useState<ControlMode>('rotation');
		const [rotationDegrees, setRotationDegrees] = useState(
			initialCropState?.rotationDegrees ?? 0,
		);
		const [isSaving, setIsSaving] = useState(false);
		const [saveError, setSaveError] = useState<string | null>(null);

		const imgRef = useRef<HTMLImageElement>(null);
		const containerRef = useRef<HTMLDivElement>(null);
		const zoomRef = useRef(1);
		const panRef = useRef({ x: 0, y: 0 });
		const imageNaturalSizeRef = useRef({ width: 0, height: 0 });
		const generatedUrlsRef = useRef<string[]>([]);
		const isApplyingTransformRef = useRef(false);
		const rotationDegreesRef = useRef(initialCropState?.rotationDegrees ?? 0);
		const hasAppliedInitialCropFrameRef = useRef(false);
		const hasUserInteractedRef = useRef(false);
		const initialZoomTargetRef = useRef(initialCropState?.zoom ?? 1);
		const pendingTransformRef = useRef<null | {
			mode: 'flip-horizontal';
			pan: { x: number; y: number };
			zoom: number;
			oldBase: { width: number; height: number };
		}>(null);
		const panStateRef = useRef({
			isActive: false,
			startX: 0,
			startY: 0,
			startPanX: 0,
			startPanY: 0,
		});
		const resizeStateRef = useRef({
			isActive: false,
			handle: 'se' as ResizeHandle,
			startX: 0,
			startY: 0,
			startWidth: 0,
			startHeight: 0,
		});

		const maxZoom = 4;

		const notifyUserInteraction = () => {
			hasUserInteractedRef.current = true;
			onUserInteraction?.();
		};

		const revokeGeneratedUrls = () => {
			generatedUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
			generatedUrlsRef.current = [];
		};

		useEffect(() => {
			revokeGeneratedUrls();
			pendingTransformRef.current = null;
			isApplyingTransformRef.current = false;
			hasUserInteractedRef.current = false;
			initialZoomTargetRef.current = initialCropState?.zoom ?? 1;
			setWorkingImageURL(imageURL);
			setPan(initialCropState?.pan || { x: 0, y: 0 });
			setZoom(initialCropState?.zoom ?? 1);
			setRotationDegrees(initialCropState?.rotationDegrees ?? 0);
			rotationDegreesRef.current = initialCropState?.rotationDegrees ?? 0;
			setIsFlippedHorizontally(initialCropState?.isFlippedHorizontally ?? false);
			setAspectRatio(initialCropState?.aspectRatio ?? initialAspectRatio);
			setCircularCrop(initialCropState?.circularCrop ?? initialCircularCrop);
			setActivePreset(initialCropState?.activePreset ?? 'free');
			setCropFrame(initialCropState?.cropFrame || { width: 320, height: 320 });
			hasAppliedInitialCropFrameRef.current = false;
		}, [imageURL, initialAspectRatio, initialCircularCrop, initialCropState]);

		useEffect(() => {
			if (!show) {
				setIsSaving(false);
				setSaveError(null);
			}
		}, [show]);

		useEffect(() => {
			return () => {
				revokeGeneratedUrls();
			};
		}, []);

		useEffect(() => {
			zoomRef.current = zoom;
		}, [zoom]);

		useEffect(() => {
			panRef.current = pan;
		}, [pan]);

		useEffect(() => {
			rotationDegreesRef.current = rotationDegrees;
		}, [rotationDegrees]);

		useEffect(() => {
			imageNaturalSizeRef.current = imageNaturalSize;
		}, [imageNaturalSize]);

		const getFrameAspect = () => {
			if (aspectRatio) return aspectRatio;
			if (imageNaturalSize.width > 0 && imageNaturalSize.height > 0) {
				return imageNaturalSize.width / imageNaturalSize.height;
			}
			return 1;
		};

		const getBaseImageSizeFor = (
			naturalWidth: number,
			naturalHeight: number,
		) => {
			const viewportWidth = imageLayerWidth
				? Math.min(containerSize.width, imageLayerWidth)
				: containerSize.width;
			const viewportHeight = imageLayerHeight
				? Math.min(containerSize.height, imageLayerHeight)
				: containerSize.height;

			if (
				!naturalWidth ||
				!naturalHeight ||
				!viewportWidth ||
				!viewportHeight
			) {
				return { width: 0, height: 0 };
			}

			const fitScale = Math.min(
				viewportWidth / naturalWidth,
				viewportHeight / naturalHeight,
			);

			const scale = imageFitMode === 'cover'
				? Math.max(viewportWidth / naturalWidth, viewportHeight / naturalHeight)
				: fitScale;

			return {
				width: naturalWidth * scale,
				height: naturalHeight * scale,
			};
		};

		const getBaseImageSize = () => {
			const { width: naturalWidth, height: naturalHeight } = imageNaturalSize;
			const viewportWidth = imageLayerWidth
				? Math.min(containerSize.width, imageLayerWidth)
				: containerSize.width;
			const viewportHeight = imageLayerHeight
				? Math.min(containerSize.height, imageLayerHeight)
				: containerSize.height;

			if (
				!naturalWidth ||
				!naturalHeight ||
				!viewportWidth ||
				!viewportHeight
			) {
				return { width: 0, height: 0 };
			}

			const fitScale = Math.min(
				viewportWidth / naturalWidth,
				viewportHeight / naturalHeight,
			);

			const scale = imageFitMode === 'cover'
				? Math.max(viewportWidth / naturalWidth, viewportHeight / naturalHeight)
				: fitScale;

			return {
				width: naturalWidth * scale,
				height: naturalHeight * scale,
			};
		};

		const getMinZoom = (baseWidth: number, baseHeight: number) => {
			if (!baseWidth || !baseHeight) return 1;

			const minForWidth = cropFrame.width / baseWidth;
			const minForHeight = cropFrame.height / baseHeight;
			const rawMinZoom = Math.max(0.1, minForWidth, minForHeight);

			if (rawMinZoom > 1 && rawMinZoom < 1.02) {
				return 1;
			}

			return rawMinZoom;
		};

		const getRotatedBounds = (
			width: number,
			height: number,
			degrees: number,
		) => {
			const radians = (degrees * Math.PI) / 180;
			const absCos = Math.abs(Math.cos(radians));
			const absSin = Math.abs(Math.sin(radians));

			return {
				width: width * absCos + height * absSin,
				height: width * absSin + height * absCos,
			};
		};

		const clampPanWithBase = (
			nextPan: { x: number; y: number },
			zoomValue: number,
			baseWidth: number,
			baseHeight: number,
			rotationValue: number = rotationDegreesRef.current,
		) => {
			if (!baseWidth || !baseHeight) {
				return nextPan;
			}

			const rotatedBounds = getRotatedBounds(
				baseWidth * zoomValue,
				baseHeight * zoomValue,
				rotationValue,
			);
			const displayedWidth = rotatedBounds.width;
			const displayedHeight = rotatedBounds.height;

			const maxOffsetX = Math.max(0, (displayedWidth - cropFrame.width) / 2);
			const maxOffsetY = Math.max(0, (displayedHeight - cropFrame.height) / 2);

			return {
				x: Math.min(maxOffsetX, Math.max(-maxOffsetX, nextPan.x)),
				y: Math.min(maxOffsetY, Math.max(-maxOffsetY, nextPan.y)),
			};
		};

		const clampPan = (nextPan: { x: number; y: number }, zoomValue: number) => {
			const baseImageSize = getBaseImageSize();
			return clampPanWithBase(
				nextPan,
				zoomValue,
				baseImageSize.width,
				baseImageSize.height,
			);
		};

		const updateCropFrame = () => {
			const { width: viewportWidth, height: viewportHeight } = containerSize;
			if (!viewportWidth || !viewportHeight) return;
			const maxFrameWidth = Math.min(
				viewportWidth,
				maxCropFrameWidth ?? Number.POSITIVE_INFINITY,
			);
			const maxFrameHeight = Math.min(
				viewportHeight,
				maxCropFrameHeight ?? Number.POSITIVE_INFINITY,
			);

			const baseSize = getBaseImageSize();

			if (!hasAppliedInitialCropFrameRef.current && !initialCropState?.cropFrame) {
				if (baseSize.width > 0 && baseSize.height > 0) {
					if (activePreset === 'free' && !aspectRatio) {
						setCropFrame({
							width: Math.max(140, Math.round(Math.min(baseSize.width, maxFrameWidth))),
							height: Math.max(140, Math.round(Math.min(baseSize.height, maxFrameHeight))),
						});
						hasAppliedInitialCropFrameRef.current = true;
						return;
					}

					const aspect = getFrameAspect();
					let nextWidth = baseSize.width;
					let nextHeight = nextWidth / aspect;

					if (nextHeight > baseSize.height) {
						nextHeight = baseSize.height;
						nextWidth = nextHeight * aspect;
					}

					nextWidth = Math.min(nextWidth, maxFrameWidth);
					nextHeight = nextWidth / aspect;

					if (nextHeight > maxFrameHeight) {
						nextHeight = maxFrameHeight;
						nextWidth = nextHeight * aspect;
					}

					setCropFrame({
						width: Math.max(140, Math.round(nextWidth)),
						height: Math.max(140, Math.round(nextHeight)),
					});
					hasAppliedInitialCropFrameRef.current = true;
					return;
				}
			}

			if (!hasAppliedInitialCropFrameRef.current && initialCropState?.cropFrame) {
				const maxWidth = Math.round(maxFrameWidth);
				const maxHeight = Math.round(maxFrameHeight);
				let restoredWidth = Math.min(maxWidth, initialCropState.cropFrame.width);
				let restoredHeight = Math.min(maxHeight, initialCropState.cropFrame.height);
				if (aspectRatio) {
					restoredHeight = restoredWidth / aspectRatio;
					if (restoredHeight > maxHeight) {
						restoredHeight = maxHeight;
						restoredWidth = restoredHeight * aspectRatio;
					}
				}
				setCropFrame({
					width: Math.max(140, Math.round(restoredWidth)),
					height: Math.max(140, Math.round(restoredHeight)),
				});
				hasAppliedInitialCropFrameRef.current = true;
				return;
			}

			if (activePreset === 'free' && !aspectRatio) {
				setCropFrame((prev) => {
					const nextFrame = {
						width: Math.max(
							140,
							Math.min(Math.round(maxFrameWidth), prev.width),
						),
						height: Math.max(
							140,
							Math.min(Math.round(maxFrameHeight), prev.height),
						),
					};

					if (
						prev.width === nextFrame.width &&
						prev.height === nextFrame.height
					) {
						return prev;
					}

					return nextFrame;
				});
				return;

			}

			const aspect = getFrameAspect();
			const maxWidth = maxFrameWidth;
			const maxHeight = maxFrameHeight;

			let nextWidth = maxWidth;
			let nextHeight = nextWidth / aspect;

			if (nextHeight > maxHeight) {
				nextHeight = maxHeight;
				nextWidth = nextHeight * aspect;
			}

			const clampedFrame = {
				width: Math.max(140, Math.round(nextWidth)),
				height: Math.max(140, Math.round(nextHeight)),
			};

			setCropFrame((prev) => {
				if (
					prev.width === clampedFrame.width &&
					prev.height === clampedFrame.height
				) {
					return prev;
				}
				return clampedFrame;
			});
		};

		const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
			const { naturalWidth, naturalHeight } = e.currentTarget;
			setImageNaturalSize({ width: naturalWidth, height: naturalHeight });

			const pending = pendingTransformRef.current;
			if (!pending) {
				return;
			}

			const newBase = getBaseImageSizeFor(naturalWidth, naturalHeight);
			const oldBase = pending.oldBase;

			if (
				!newBase.width ||
				!newBase.height ||
				!oldBase.width ||
				!oldBase.height
			) {
				pendingTransformRef.current = null;
				isApplyingTransformRef.current = false;
				return;
			}

			const nextMinZoom = getMinZoom(newBase.width, newBase.height);
			const nextZoom = Math.min(maxZoom, Math.max(nextMinZoom, pending.zoom));

			let shiftedPan = pending.pan;
			if (pending.mode === 'flip-horizontal') {
				shiftedPan = {
					x:
						-pending.pan.x *
						((newBase.width * nextZoom) / (oldBase.width * pending.zoom || 1)),
					y:
						pending.pan.y *
						((newBase.height * nextZoom) /
							(oldBase.height * pending.zoom || 1)),
				};
			}

			setZoom(nextZoom);
			setPan(
				clampPanWithBase(
					shiftedPan,
					nextZoom,
					newBase.width,
					newBase.height,
					rotationDegreesRef.current,
				),
			);
			pendingTransformRef.current = null;
			isApplyingTransformRef.current = false;
		};

		const handlePanStart = (e: React.PointerEvent<HTMLDivElement>) => {
			if (resizeStateRef.current.isActive) return;
			if (e.button !== 0) return;

			const container = containerRef.current;
			if (!container) return;

			const baseImageSize = getBaseImageSize();
			const rotatedBounds = getRotatedBounds(
				baseImageSize.width * zoomRef.current,
				baseImageSize.height * zoomRef.current,
				rotationDegreesRef.current,
			);
			const canPan =
				rotatedBounds.width > cropFrame.width ||
				rotatedBounds.height > cropFrame.height;

			if (!canPan) return;

			panStateRef.current = {
				isActive: true,
				startX: e.clientX,
				startY: e.clientY,
				startPanX: panRef.current.x,
				startPanY: panRef.current.y,
			};

			notifyUserInteraction();
			setIsPanning(true);
			container.setPointerCapture(e.pointerId);
			e.preventDefault();
		};

		const handlePanMove = (e: React.PointerEvent<HTMLDivElement>) => {
			if (resizeStateRef.current.isActive) {
				const { width: viewportWidth, height: viewportHeight } = containerSize;
				if (!viewportWidth || !viewportHeight) return;

				const ratio = activePreset === 'free' ? undefined : aspectRatio || 1;
				const state = resizeStateRef.current;
				const dx = e.clientX - state.startX;
				const dy = e.clientY - state.startY;

				let width = state.startWidth;
				let height = state.startHeight;

				if (state.handle.includes('e')) width += dx * 2;
				if (state.handle.includes('w')) width -= dx * 2;
				if (state.handle.includes('s')) height += dy * 2;
				if (state.handle.includes('n')) height -= dy * 2;

				if (ratio) {
					const useWidthDriver =
						state.handle === 'e' ||
						state.handle === 'w' ||
						((state.handle.includes('e') || state.handle.includes('w')) &&
							Math.abs(dx) >= Math.abs(dy));

					if (useWidthDriver) {
						height = width / ratio;
					} else {
						width = height * ratio;
					}
				}

				if (ratio) {
					const maxWidth = Math.min(
						viewportWidth,
						maxCropFrameWidth ?? Number.POSITIVE_INFINITY,
					);
					const maxHeight = Math.min(
						viewportHeight,
						maxCropFrameHeight ?? Number.POSITIVE_INFINITY,
					);
					const minWidth = Math.max(140, 140 * ratio);
					width = Math.max(minWidth, width);
					height = width / ratio;

					if (height < 140) {
						height = 140;
						width = height * ratio;
					}

					if (width > maxWidth) {
						width = maxWidth;
						height = width / ratio;
					}

					if (height > maxHeight) {
						height = maxHeight;
						width = height * ratio;
					}
				} else {
					const maxWidth = Math.min(
						viewportWidth,
						maxCropFrameWidth ?? Number.POSITIVE_INFINITY,
					);
					const maxHeight = Math.min(
						viewportHeight,
						maxCropFrameHeight ?? Number.POSITIVE_INFINITY,
					);
					width = Math.max(140, Math.min(maxWidth, width));
					height = Math.max(140, Math.min(maxHeight, height));
				}

				setCropFrame({
					width: Math.round(width),
					height: Math.round(height),
				});
				notifyUserInteraction();
				return;
			}

			const panState = panStateRef.current;
			if (!panState.isActive) return;

			const dx = e.clientX - panState.startX;
			const dy = e.clientY - panState.startY;

			setPan(
				clampPan(
					{
						x: panState.startPanX + dx,
						y: panState.startPanY + dy,
					},
					zoomRef.current,
				),
			);
			notifyUserInteraction();
		};

		const handlePanEnd = (e: React.PointerEvent<HTMLDivElement>) => {
			const container = containerRef.current;
			if (container && container.hasPointerCapture(e.pointerId)) {
				container.releasePointerCapture(e.pointerId);
			}

			panStateRef.current.isActive = false;
			setIsPanning(false);

			resizeStateRef.current.isActive = false;
			setIsResizing(false);
		};

		const handleResizeStart = (
			e: React.PointerEvent<HTMLButtonElement>,
			handle: ResizeHandle,
		) => {
			if (e.button !== 0) return;

			const container = containerRef.current;
			if (!container) return;

			resizeStateRef.current = {
				isActive: true,
				handle,
				startX: e.clientX,
				startY: e.clientY,
				startWidth: cropFrame.width,
				startHeight: cropFrame.height,
			};

			panStateRef.current.isActive = false;
			notifyUserInteraction();
			setIsPanning(false);
			setIsResizing(true);
			container.setPointerCapture(e.pointerId);
			e.preventDefault();
			e.stopPropagation();
		};

		const handleWheelZoom = (e: React.WheelEvent<HTMLDivElement>) => {
			const container = containerRef.current;
			if (!container) return;

			e.preventDefault();

			const baseImageSize = getBaseImageSize();
			const minZoomValue = Math.min(
				1,
				getMinZoom(baseImageSize.width, baseImageSize.height),
			);
			const previousZoom = zoomRef.current;
			const deltaScale = Math.exp(-e.deltaY * 0.002);
			const rawNextZoom = previousZoom * deltaScale;
			const nextZoom = Math.min(
				maxZoom,
				Math.max(minZoomValue, Number(rawNextZoom.toFixed(3))),
			);

			if (Math.abs(nextZoom - previousZoom) < 0.0001) {
				return;
			}

			const containerRect = container.getBoundingClientRect();
			const pointerX =
				e.clientX - containerRect.left - container.clientWidth / 2;
			const pointerY =
				e.clientY - containerRect.top - container.clientHeight / 2;

			const currentPan = panRef.current;
			const zoomRatio = nextZoom / previousZoom;

			const zoomAnchoredPan = {
				x: pointerX - (pointerX - currentPan.x) * zoomRatio,
				y: pointerY - (pointerY - currentPan.y) * zoomRatio,
			};

			setZoom(nextZoom);
			setPan(clampPan(zoomAnchoredPan, nextZoom));
			notifyUserInteraction();
		};

		const handleFlipHorizontal = () => {
			setIsFlippedHorizontally((prev) => !prev);
			setPan((prevPan) => clampPan({ x: -prevPan.x, y: prevPan.y }, zoomRef.current));
			notifyUserInteraction();
		};

		const handleRotationSliderChange = (value: number) => {
			rotationDegreesRef.current = value;
			setRotationDegrees(value);
			notifyUserInteraction();
		};

		const normalizeRotation = (value: number) => {
			let normalized = value % 360;
			if (normalized > 180) normalized -= 360;
			if (normalized <= -180) normalized += 360;
			return normalized;
		};

		const handleRotateLeft = () => {
			setRotationDegrees((prev) => {
				const next = normalizeRotation(prev - 90);
				rotationDegreesRef.current = next;
				return next;
			});
			notifyUserInteraction();
		};

		const handleReset = () => {
			if (isApplyingTransformRef.current) {
				return;
			}

			const baseImageSize = getBaseImageSize();
			const minZoomValue = Math.min(
				1,
				getMinZoom(baseImageSize.width, baseImageSize.height),
			);
			const resetZoom = Number(
				Math.min(
					maxZoom,
					Math.max(minZoomValue, initialZoomTargetRef.current),
				).toFixed(3),
			);

			revokeGeneratedUrls();
			pendingTransformRef.current = null;
			setWorkingImageURL(imageURL);
			setPan({ x: 0, y: 0 });
			setZoom(resetZoom);
			setIsFlippedHorizontally(false);
			setRotationDegrees(0);
			rotationDegreesRef.current = 0;
			notifyUserInteraction();
		};

		const handlePresetChange = (preset: AspectPreset) => {
			setActivePreset(preset);
			notifyUserInteraction();

			switch (preset) {
			case 'free':
				setAspectRatio(undefined);
				setCircularCrop(false);
				break;
			case 'square':
				setAspectRatio(1);
				setCircularCrop(false);
				break;
			case '16:9':
				setAspectRatio(16 / 9);
				setCircularCrop(false);
				break;
			case '4:3':
				setAspectRatio(4 / 3);
				setCircularCrop(false);
				break;
			case '4:5':
				setAspectRatio(4 / 5);
				setCircularCrop(false);
				break;
			case 'circle':
				setAspectRatio(1);
				setCircularCrop(true);
				break;
			}
		};

		useEffect(() => {
			const container = containerRef.current;
			if (!container) return;

			const updateContainerSize = () => {
				setContainerSize({
					width: container.clientWidth,
					height: container.clientHeight,
				});
			};

			updateContainerSize();
			const resizeObserver = new ResizeObserver(updateContainerSize);
			resizeObserver.observe(container);

			return () => {
				resizeObserver.disconnect();
			};
		}, []);

		useEffect(() => {
			if (show && containerRef.current) {
				setContainerSize({
					width: containerRef.current.clientWidth,
					height: containerRef.current.clientHeight,
				});
			}
		}, [show]);

		useEffect(() => {
			updateCropFrame();
		}, [
			activePreset,
			aspectRatio,
			imageNaturalSize.width,
			imageNaturalSize.height,
			containerSize.width,
			containerSize.height,
		]);

		const baseImageSize = getBaseImageSize();
		const rawMinZoom = getMinZoom(baseImageSize.width, baseImageSize.height);
		// The crop opens at 100% and the slider can always reach it. minZoom
		// (frame / fitted image) briefly computes above 1 while the frame is
		// still being sized, and using it as the floor pinned the zoom there for
		// the life of the modal. It may only lower the floor, never raise it.
		const minZoom = Math.min(1, rawMinZoom);

		useEffect(() => {
			setZoom((prevZoom) => {
				const zoomSource = hasUserInteractedRef.current
					? prevZoom
					: initialZoomTargetRef.current;
				const clampedZoom = Math.min(maxZoom, Math.max(minZoom, zoomSource));
				return Number(clampedZoom.toFixed(3));
			});
		}, [minZoom]);

		useEffect(() => {
			setPan((prevPan) => clampPan(prevPan, zoom));
		}, [
			zoom,
			rotationDegrees,
			cropFrame.width,
			cropFrame.height,
			imageNaturalSize.width,
			imageNaturalSize.height,
			containerSize.width,
			containerSize.height,
		]);

		const getCroppedImageBlob = async (): Promise<{
			blob: Blob;
			mimeType: 'image/png' | 'image/jpeg';
		} | null> => {
			if (!imgRef.current || !containerRef.current) {
				return null;
			}

			const hasOutputDimensions =
				typeof outputWidth !== 'undefined' || typeof outputHeight !== 'undefined';
			const hasValidOutputDimensions =
				typeof outputWidth === 'number' &&
				typeof outputHeight === 'number' &&
				outputWidth > 0 &&
				outputHeight > 0;

			if (hasOutputDimensions && !hasValidOutputDimensions) {
				throw new Error(
					t(
						'Both outputWidth and outputHeight must be provided as positive numbers.',
					),
				);
			}

			const image = imgRef.current;
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');

			if (!ctx) {
				return null;
			}

			const displayedWidth = baseImageSize.width * zoom;
			const displayedHeight = baseImageSize.height * zoom;

			if (!displayedWidth || !displayedHeight) {
				return null;
			}

			const displayScale = displayedWidth / image.naturalWidth;
			const cropPixelWidth = Math.max(
				1,
				Math.round(cropFrame.width / displayScale),
			);
			const cropPixelHeight = Math.max(
				1,
				Math.round(cropFrame.height / displayScale),
			);

			canvas.width = cropPixelWidth;
			canvas.height = cropPixelHeight;

			const rotationRadians = (rotationDegrees * Math.PI) / 180;
			const centerX = (cropFrame.width / 2 + pan.x) / displayScale;
			const centerY = (cropFrame.height / 2 + pan.y) / displayScale;

			ctx.save();
			ctx.translate(centerX, centerY);
			ctx.rotate(rotationRadians);
			if (isFlippedHorizontally) {
				ctx.scale(-1, 1);
			}
			ctx.drawImage(
				image,
				-image.naturalWidth / 2,
				-image.naturalHeight / 2,
				image.naturalWidth,
				image.naturalHeight,
			);
			ctx.restore();

			if (circularCrop) {
				const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
				const data = imageData.data;
				const cropCenterX = canvas.width / 2;
				const cropCenterY = canvas.height / 2;
				const radius = Math.min(cropCenterX, cropCenterY);

				for (let y = 0; y < canvas.height; y++) {
					for (let x = 0; x < canvas.width; x++) {
						const dx = x - cropCenterX;
						const dy = y - cropCenterY;
						const distance = Math.sqrt(dx * dx + dy * dy);

						if (distance > radius) {
							const i = (y * canvas.width + x) * 4;
							data[i + 3] = 0;
						}
					}
				}

				ctx.putImageData(imageData, 0, 0);
			}

			if (hasValidOutputDimensions) {
				const cropAspect =
					typeof aspectRatio === 'number' && aspectRatio > 0
						? aspectRatio
						: cropFrame.width / cropFrame.height;
				const targetAspect = outputWidth / outputHeight;
				const aspectTolerance = 0.01;

				if (circularCrop && Math.abs(targetAspect - 1) > aspectTolerance) {
					throw new Error(
						t(
							'Circular crops require equal outputWidth and outputHeight to avoid distortion.',
						),
					);
				}

				if (Math.abs(targetAspect - cropAspect) > aspectTolerance) {
					throw new Error(
						t(
							'Output dimensions must match the selected crop aspect ratio to avoid distortion.',
						),
					);
				}
			}

			let finalCanvas: HTMLCanvasElement = canvas;
			if (hasValidOutputDimensions) {
				const targetCanvas = document.createElement('canvas');
				targetCanvas.width = outputWidth;
				targetCanvas.height = outputHeight;
				const targetCtx = targetCanvas.getContext('2d');
				if (targetCtx) {
					targetCtx.drawImage(canvas, 0, 0, outputWidth, outputHeight);
					finalCanvas = targetCanvas;
				}
			}

			const toBlob = (type: 'image/png' | 'image/jpeg', quality?: number) => {
				return new Promise<Blob | null>((resolve) => {
					finalCanvas.toBlob((blob) => resolve(blob), type, quality);
				});
			};

			const toBlobFromDataUrl = async (
				type: 'image/png' | 'image/jpeg',
				quality?: number,
			) => {
				try {
					const dataUrl = finalCanvas.toDataURL(type, quality);
					const response = await fetch(dataUrl);
					const blob = await response.blob();

					if (!blob || blob.size === 0) {
						return null;
					}

					return blob;
				} catch {
					return null;
				}
			};

			const requestedType: 'image/png' | 'image/jpeg' = circularCrop
				? 'image/png'
				: outputMimeType;
			const requestedQuality = requestedType === 'image/jpeg' ? 0.9 : undefined;

			const primaryBlob = await toBlob(requestedType, requestedQuality);
			if (
				primaryBlob &&
				(primaryBlob.type === requestedType || primaryBlob.type === '')
			) {
				return { blob: primaryBlob, mimeType: requestedType };
			}

			const forcedBlob = await toBlobFromDataUrl(
				requestedType,
				requestedQuality,
			);
			if (forcedBlob) {
				return { blob: forcedBlob, mimeType: requestedType };
			}

			if (requestedType === 'image/jpeg') {
				const jpegBlob = await toBlob('image/jpeg', 0.9);
				if (
					jpegBlob &&
					(jpegBlob.type === 'image/jpeg' || jpegBlob.type === '')
				) {
					return { blob: jpegBlob, mimeType: 'image/jpeg' };
				}
			}

			const pngBlob =
				(await toBlobFromDataUrl('image/png')) || (await toBlob('image/png'));
			if (!pngBlob || pngBlob.size === 0) {
				return null;
			}

			return { blob: pngBlob, mimeType: 'image/png' };
		};

		const getCurrentCropState = (): BayImageCropState => ({
			activePreset,
			aspectRatio,
			circularCrop,
			cropFrame: { ...cropFrame },
			isFlippedHorizontally,
			pan: { ...pan },
			rotationDegrees,
			zoom,
		});

		const handleSave = async () => {
			setIsSaving(true);
			setSaveError(null);

			try {
				if (onSave) {
					await onSave();
					onCropStateSaved?.(getCurrentCropState());
					return;
				}

				const result = await getCroppedImageBlob();
				if (!result) {
					setSaveError(t('Failed to crop image. Please try again.'));
					return;
				}

				onCropStateSaved?.(getCurrentCropState());
				onCropComplete(result.blob, result.mimeType);
			} catch (error) {
				setSaveError(
					error instanceof Error
						? error.message
						: t('Failed to save cropped image. Please try again.'),
				);
			} finally {
				setIsSaving(false);
			}
		};

		useImperativeHandle(ref, () => ({
			triggerSave: handleSave,
		}));

		const isProcessing = isUploading || isSaving;
		const resizeHandles: ResizeHandle[] = circularCrop
			? ['ne', 'nw', 'se', 'sw']
			: ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

		const cropperContent = (
			<div
				className={['bay-image-crop-main-stack h-100 d-flex flex-column', cropperContentClassName].filter(Boolean).join(' ')}
				style={{ minHeight: 0, ...cropperContentStyle }}
			>
				{saveError && (
					<Alert
						variant="danger"
						className="mb-3"
						dismissible
						onClose={() => setSaveError(null)}
					>
						<strong>{t('Error:')}</strong> {saveError}
					</Alert>
				)}

				<div className={`mb-4 ${hideWarningAlert ? 'd-none' : ''}`}>
					{t('Cropping is permanent. The original image will be replaced with the cropped version.')}
				</div>

				<div className={`d-flex justify-content-center mb-4 ${showCropShapeControlOptions ? '' : 'd-none'}`}>
					<div className="d-flex justify-content-center bg-blue-50 rounded-pill p-1">
						{(Object.keys(PRESET_LABELS) as AspectPreset[]).map((preset) => (
							<BayButton
								key={preset}
								className="d-flex align-items-center px-3 py-2 fw-bold cursor-pointer rounded-pill border-0"
								onClick={() => handlePresetChange(preset)}
								variant={activePreset === preset ? 'primary' : 'link'}
							>
								{React.createElement(PRESET_ICONS[preset], {
									className: `${styles.cropShapeIcon} me-2`,
									size: 14,
								})}
								<span className="fw-bold">{t(PRESET_LABELS[preset])}</span>
							</BayButton>
						))}
					</div>
				</div>

				{(() => {
					const controlsBlock = (
						<>
							<ToolbarControls
								t={t}
								isProcessing={isProcessing}
								showCropShapeControl={showCropShapeControl}
								activePreset={activePreset}
								presetLabels={PRESET_LABELS}
								presetIcons={PRESET_ICONS}
								onPresetChange={handlePresetChange}
								onRotateLeft={handleRotateLeft}
								onFlipHorizontal={handleFlipHorizontal}
								onReset={handleReset}
							/>
							<AdjustmentControls
								t={t}
								isProcessing={isProcessing}
								sliderLayout={sliderLayout}
								controlMode={controlMode}
								onControlModeChange={setControlMode}
								isSplitRotationView={isSplitRotationView}
								rotationDegrees={rotationDegrees}
								onRotationChange={handleRotationSliderChange}
								showRotationTickMarks={showRotationTickMarks}
								minZoom={minZoom}
								maxZoom={maxZoom}
								zoom={zoom}
								zoomDisplayOffsetPercent={zoomDisplayOffsetPercent}
								onZoomChange={(value) => {
									setZoom(value);
									notifyUserInteraction();
								}}
								showScaleTickMarks={showScaleTickMarks}
								showScaleRangeLabels={showScaleRangeLabels}
							/>
						</>
					);
					return controlPlacement === 'top' ? controlsBlock : null;
				})()}

				<div
					ref={containerRef}
					className={[styles.cropContainer, cropContainerClassName].filter(Boolean).join(' ')}
					onPointerDown={handlePanStart}
					onPointerMove={handlePanMove}
					onPointerUp={handlePanEnd}
					onPointerCancel={handlePanEnd}
					onWheel={handleWheelZoom}
					style={{
						...cropContainerStyle,
						cursor: isResizing
							? 'nwse-resize'
							: isPanning
								? 'grabbing'
								: 'grab',
					}}
				>
					<div className={styles.zoomViewport}>
						<div

							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								overflow: 'hidden',
								width: imageLayerWidth ? `${Math.min(containerSize.width, imageLayerWidth)}px` : '100%',
								height: imageLayerHeight ? `${Math.min(containerSize.height, imageLayerHeight)}px` : '100%',
							}}
						>
							<div
								className={styles.zoomStage}
								style={{
									width: `${baseImageSize.width}px`,
									height: `${baseImageSize.height}px`,
									transform: `translate3d(${pan.x}px, ${pan.y}px, 0) rotate(${rotationDegrees}deg) scale(${isFlippedHorizontally ? -zoom : zoom}, ${zoom})`,
									transition: isPanning ? 'none' : 'transform 0.06s linear',
								}}
							>
								<img
									ref={imgRef}
									src={workingImageURL}
									onLoad={handleImageLoad}
									crossOrigin="anonymous"
									style={{
										width: '100%',
										height: '100%',
										objectFit: 'contain',
										display: 'block',
										userSelect: 'none',
									}}
									draggable={false}
									alt={t('Crop preview')}
								/>
							</div>
						</div>
					</div>

					<div
						className={styles.cropFrame}
						style={{
							width: cropFrame.width,
							height: cropFrame.height,
							borderRadius: circularCrop ? '50%' : '0.375rem',
						}}
					>
						{!circularCrop && <div className={styles.thirdsVerticalOne} />}
						{!circularCrop && <div className={styles.thirdsVerticalTwo} />}
						{!circularCrop && <div className={styles.thirdsHorizontalOne} />}
						{!circularCrop && <div className={styles.thirdsHorizontalTwo} />}
						{resizeHandles.map((handle) => (
							<button
								key={handle}
								type="button"
								aria-label={`resize-${handle}`}
								className={`${styles.resizeHandle} ${styles[`resizeHandle_${handle}`]}`}
								onPointerDown={(e) => handleResizeStart(e, handle)}
								disabled={isProcessing}
							/>
						))}
					</div>
				</div>

				{controlPlacement === 'bottom' ? (
					<>
						<ToolbarControls
							t={t}
							isProcessing={isProcessing}
							showCropShapeControl={showCropShapeControl}
							activePreset={activePreset}
							presetLabels={PRESET_LABELS}
							presetIcons={PRESET_ICONS}
							onPresetChange={handlePresetChange}
							onRotateLeft={handleRotateLeft}
							onFlipHorizontal={handleFlipHorizontal}
							onReset={handleReset}
						/>
						<AdjustmentControls
							t={t}
							isProcessing={isProcessing}
							sliderLayout={sliderLayout}
							controlMode={controlMode}
							onControlModeChange={setControlMode}
							rotationDegrees={rotationDegrees}
							onRotationChange={handleRotationSliderChange}
							showRotationTickMarks={showRotationTickMarks}
							minZoom={minZoom}
							maxZoom={maxZoom}
							zoom={zoom}
							zoomDisplayOffsetPercent={zoomDisplayOffsetPercent}
							onZoomChange={(value) => {
								setZoom(value);
								notifyUserInteraction();
							}}
							showScaleTickMarks={showScaleTickMarks}
							showScaleRangeLabels={showScaleRangeLabels}
							isSplitRotationView={isSplitRotationView}
						/>
					</>
				) : null}
			</div>
		);

		const modalSize = size || 'xl';
		const modalFullscreen = fullscreen;

		const renderLayout = () => {
			switch (layout) {
			case 'split':
				return (
					<SplitLayout
						cropperContent={cropperContent}
						splitContent={splitContent}
						t={t}
						splitLayoutClassName={splitLayoutClassName}
						splitLayoutStyle={splitLayoutStyle}
						splitMainStyle={splitMainStyle}
					/>
				);
			case 'wide':
			default:
				return <WideLayout cropperContent={cropperContent} />;
			}
		};

		if (embedded) {
			return show ? renderLayout() : null;
		}

		const customFooter = (
			<>
				<Button
					variant="outline-secondary"
					onClick={onHide}
					disabled={isProcessing}
				>
					{t('Cancel')}
				</Button>
				<Button
					variant="primary"
					onClick={handleSave}
					disabled={isProcessing}
					className="ms-3"
				>
					{isProcessing ? t('Saving...') : t('Upload')}
				</Button>
			</>
		);

		return (
			<BayModal
				closeButton={!isProcessing}
				show={show}
				onHideFunc={onHide}
				title={title || t('Crop Image')}
				size={modalSize}
				fullscreen={modalFullscreen as true | string | undefined}
				backdrop="static"
				bodyClass="border-top"
				hideFooter={false}
				footer={customFooter}
				centered
				height="92vh"
			>
				<div className="position-relative h-100">
					{isProcessing && (
						<div
							className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-3"
							style={{
								backgroundColor: 'rgba(255, 255, 255, 0.95)',
								// eslint-disable-next-line no-restricted-syntax -- grandfathered: raw z-index fenced as step 2b, the portal band is a cross-repo decision. See docs/css-unification/overlay-and-zindex.md
								zIndex: 1050,
							}}
						>
							<BaySpinner
								hideTitle
								size={40}
								variant="primary"
							/>
							<div className="text-700 fw-bold">
								{t('Saving cropped image...')}
							</div>
						</div>
					)}
					{renderLayout()}
				</div>
			</BayModal>
		);
	},
);

BayImageCropModal.displayName = 'BayImageCropModal';

export default BayImageCropModal;
